import { Box, Typography } from "@mui/material";
import { Fragment } from "react";

import { ExtractionDraftValue } from "./types";

export interface RecordDiffTooltipProps {
  /** What the record holds today, in display form. */
  recordDisplay: string;
  /** What this review would write instead, in display form. */
  readingDisplay: string;
  /** Takes one of the two values into the field. */
  onTake: (value: ExtractionDraftValue) => void;
  recordValue: ExtractionDraftValue;
  readingValue: ExtractionDraftValue;
  /** Appended when the extractor was also unsure, so one icon carries both. */
  lowConfidenceNote?: string;
}

/**
 * Tooltip body for a value that disagrees with the record it is about to
 * replace: the two values side by side, each one clickable to take it into the
 * field.
 *
 * Laid out like `SourcesDisagreeTooltip`, which the manifest review already uses
 * for the same question, so a reviewer meets one idiom for "these two disagree"
 * across the app. It takes the value rather than copying it, because unlike the
 * manifest case the field is right here and editable.
 */
export function RecordDiffTooltip({
  recordDisplay,
  readingDisplay,
  onTake,
  recordValue,
  readingValue,
  lowConfidenceNote,
}: RecordDiffTooltipProps) {
  const rows: { label: string; display: string; value: ExtractionDraftValue }[] = [
    { label: "On the record", display: recordDisplay, value: recordValue },
    { label: "From this document", display: readingDisplay, value: readingValue },
  ];

  return (
    <Box>
      <Typography variant="caption" sx={{ fontWeight: 700, display: "block", mb: 0.5 }}>
        This would replace what the record holds
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr",
          columnGap: 1.5,
          rowGap: 0.25,
          alignItems: "baseline",
        }}
      >
        {rows.map((row) => (
          <Fragment key={row.label}>
            <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
              {row.label}
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() => onTake(row.value)}
              aria-label={`Use the value ${row.label.toLowerCase()}: ${row.display}`}
              title="Click to use this value"
              sx={{
                appearance: "none",
                border: "none",
                background: "none",
                p: 0,
                m: 0,
                font: "inherit",
                color: "inherit",
                textAlign: "left",
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <Typography variant="caption" component="span">
                {row.display}
              </Typography>
            </Box>
          </Fragment>
        ))}
      </Box>
      {lowConfidenceNote != null && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.5, opacity: 0.7 }}>
          {lowConfidenceNote}
        </Typography>
      )}
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, opacity: 0.7, fontStyle: "italic" }}
      >
        Click a value to use it
      </Typography>
    </Box>
  );
}
